import * as Sqrl from 'squirrelly';

import {getSizedImageUrl} from '../vendor/theme-scripts/theme-images';
import {getUrlWithVariant} from '../vendor/theme-scripts/theme-product-form';
import {getProductJson} from '../util/fetch-product';
import loadScript from '../util/loader';

const defaults = {
  color: 'ash',
};

const selectors = {
  swatch: 'data-swatch',
  outerGrid: '[data-grid-item]',
  slide: '[data-grid-slide]',
  image: 'data-swatch-image',
  variant: 'data-swatch-variant',
  button: '[data-swatch-button]',
  link: '[data-grid-link]',
  wrapper: '[data-grid-swatches]',
  template: '[data-swatch-template]',
  handle: 'data-swatch-handle',
  label: 'data-swatch-label',
};

class ColorMatch {
  constructor(options = {}) {
    this.settings = {
      ...defaults,
      ...options,
    };

    this.match = this.init();
  }

  getColor() {
    return this.match;
  }

  init() {
    const getColors = loadScript({json: window.theme.assets.swatches});
    return getColors
      .then((colors) => {
        return this.matchColors(colors, this.settings.color);
      })
      .catch((e) => {
        console.log('failed to load swatch colors script');
        console.log(e);
      });
  }

  matchColors(colors, name) {
    let bg = '#E5E5E5';
    let img = null;
    const path = window.theme.assets.base || '/';
    const comparisonName = name.toLowerCase().replace(/\s/g, '');
    const array = colors.colors;
    if (array) {
      const variantNameMatch = (nameObject) => {
        const indexName = Object.keys(nameObject).toString();
        const neatName = indexName.toLowerCase().replace(/\s/g, '');
        return neatName === comparisonName;
      };
      const position = array.findIndex(variantNameMatch);
      if (position > -1) {
        const value = Object.values(array[position])[0];
        if (value.includes('.jpg') || value.includes('.jpeg') || value.includes('.png') || value.includes('.svg')) {
          img = `${path}${value}`;
          bg = '#888888';
        } else {
          bg = value;
        }
      }
    }
    return {
      color: this.settings.color,
      path: img,
      hex: bg,
    };
  }
}

class Swatch {
  constructor(element) {
    this.element = element;
    this.outer = this.element.closest(selectors.outerGrid);
    this.colorString = element.getAttribute(selectors.swatch);
    this.image = element.getAttribute(selectors.image);
    this.variant = element.getAttribute(selectors.variant);
    const matcher = new ColorMatch({color: this.colorString});
    matcher.getColor().then((result) => {
      this.colorMatch = result;
      this.init();
    });
  }

  init() {
    this.setStyles();
    if (this.variant && this.outer) {
      this.handleClicks();
    }
  }

  setStyles() {
    if (this.colorMatch.hex) {
      this.element.style.setProperty('--swatch', `${this.colorMatch.hex}`);
    }
    if (this.colorMatch.path) {
      this.element.style.setProperty('background-image', `url(${this.colorMatch.path})`);
      this.element.style.setProperty('background-size', 'cover');
    }
  }

  handleClicks() {
    this.slide = this.outer.querySelector(selectors.slide);
    this.linkElement = this.outer.querySelector(selectors.link);
    this.linkDestination = getUrlWithVariant(this.linkElement.getAttribute('href'), this.variant);
    this.button = this.element.closest(selectors.button);
    this.button.addEventListener(
      'click',
      function () {
        this.linkElement.setAttribute('href', this.linkDestination);
        this.slide.setAttribute('src', this.linkDestination);
        if (this.image) {
          // container width rounded to the nearest 180 pixels
          // increses likelihood that the image will be cached
          let ratio = window.devicePixelRatio || 1;
          let pixels = this.slide.offsetWidth * ratio;
          let widthRounded = Math.ceil(pixels / 180) * 180;
          let sizedImage = getSizedImageUrl(this.image, `${widthRounded}x`);
          window
            .fetch(sizedImage)
            .then((response) => {
              return response.blob();
            })
            .then((blob) => {
              var objectURL = URL.createObjectURL(blob);
              this.slide.style.setProperty('background-color', '#fff');
              this.slide.style.setProperty('background-image', `url("${objectURL}")`);
            })
            .catch((error) => {
              console.log(`Error: ${error}`);
            });
        }
      }.bind(this)
    );
  }
}

class GridSwatch {
  constructor(wrap) {
    this.template = document.querySelector(selectors.template).innerHTML;
    this.wrap = wrap;
    this.handle = wrap.getAttribute(selectors.handle);
    const label = wrap.getAttribute(selectors.label).trim().toLowerCase();
    getProductJson(this.handle).then((product) => {
      this.product = product;
      this.colorOption = product.options.find(function (element) {
        return element.name.toLowerCase() === label || null;
      });
      if (this.colorOption) {
        this.swatches = this.colorOption.values;
        this.init();
      }
    });
  }

  init() {
    this.wrap.innerHTML = '';
    this.swatches.forEach((swatch) => {
      let variant = this.product.variants.find((variant) => {
        return variant.options.includes(swatch);
      });
      const image = variant.featured_media ? variant.featured_media.preview_image.src : '';

      const rand = Math.floor(Math.random() * 9999);

      this.wrap.innerHTML += Sqrl.render(this.template, {
        color: swatch,
        uniq: `${this.product.id}-${variant.id}-${rand}`,
        variant: variant.id,
        image,
      });
    });
    this.swatchElements = this.wrap.querySelectorAll(`[${selectors.swatch}]`);
    this.swatchElements.forEach((el) => {
      new Swatch(el);
    });
  }
}

export function getSwatch(name) {
  const getColors = loadScript({json: window.theme.assets.swatches});
  return getColors
    .then((colors) => {
      const swatch = buildSwatch(colors, name);
      return swatch;
    })
    .catch((e) => {
      console.log('failed to load swatch colors script');
      console.log(e);
    });
}

function buildSwatch(colors, name) {
  let bg = '#E5E5E5';
  let img = null;
  const path = window.theme.assets.base || '/';
  const comparisonName = name.toLowerCase().replace(/\s/g, '');
  const array = colors.colors;
  if (array) {
    const variantNameMatch = (nameObject) => {
      const indexName = Object.keys(nameObject).toString();
      const neatName = indexName.toLowerCase().replace(/\s/g, '');
      return neatName === comparisonName;
    };
    const position = array.findIndex(variantNameMatch);
    if (position > -1) {
      const value = Object.values(array[position])[0];
      if (value.includes('.jpg') || value.includes('.jpeg') || value.includes('.png') || value.includes('.svg')) {
        img = `${path}${value}`;
        bg = '#888888';
      } else {
        bg = value;
      }
    }
  }
  return {
    path: img,
    hex: bg,
  };
}

function makeGridSwatches(container) {
  const gridSwatchWrappers = container.querySelectorAll(selectors.wrapper);
  gridSwatchWrappers.forEach((wrap) => {
    new GridSwatch(wrap);
  });
}

const swatchSection = {
  onLoad() {
    this.swatches = [];
    const els = this.container.querySelectorAll(`[${selectors.swatch}]`);
    els.forEach((el) => {
      this.swatches.push(new Swatch(el));
    });
  },
};

const swatchGridSection = {
  onLoad() {
    makeGridSwatches(this.container);
  },
};

export {swatchGridSection, swatchSection, makeGridSwatches, Swatch};
